import { get } from 'lodash';

// Utils
import { animateScrollPromise } from '../../../utils/animation/animateScrollIntoView';
import getParentScrollableSection, {
    getParentScrollableSectionForElement,
} from '../../../utils/dom/getParentScrollableSection';
import { length, prop } from '../../../../common/utils/immutableHelper';
import scrollExcessHandler, { ScrollCleanupConfig } from './scrollExcessHandler';
import {
    getContentDomElement,
    getTargetScrollToAlignElementToCenter,
    getTargetScrollToAlignElementToEdge,
    ScrollToElementConfig,
} from './scrollToElementUtils';
import { getSnapPointSheetHeight } from '../../structural/sheet/utils/sheetHeightUtils';
import virtualKeyboardHeightSingleton from '../../../utils/keyboard/virtualKeyboardHeightSingleton';

// Selectors
import { selectIsMobileAppMode } from '../../../platform/platformSelector';
import { getSelectedElementIds } from '../../../element/selection/selectedElementsSelector';
import {
    getCurrentlyEditingEditorKey,
    getCurrentlyEditingId,
} from '../../../element/selection/currentlyEditingSelector';
import { getSheetState } from '../../structural/sheet/sheetSelectors';

// Constants
import {
    ELEMENT_EDIT_COMPLETE,
    ELEMENT_EDIT_START,
    ELEMENTS_DESELECT_ALL,
    ELEMENTS_DESELECTED,
} from '../../../../common/elements/selectionConstants';
import {
    DOCUMENT_MODAL_EDITOR_KEY,
    DOCUMENT_MODAL_TITLE_EDITOR_KEY,
} from '../../../element/document/modal/documentModalConstants';

// Types
import { UnknownAction } from 'redux';
import { ReduxStore } from '../../../types/reduxTypes';
import { SheetActionTypes, SheetUpdateActiveSnapPointAction } from '../../structural/sheet/sheetActionTypes';
import { Rect } from '../../../../common/maths/geometry/rect/rectTypes';
import { MOBILE_TOOLBAR_HEIGHT, ToolbarLevel } from '../../toolbar/MobileToolbarConstants';
import { TABLE_CELL_EDITOR_KEY, TABLE_GRID_EDITOR_KEY } from '../../../../common/table/tableConstants';
import { CANVAS_VIEWPORT_ID } from '../../../canvas/utils/canvasConstants';
import { POPUP_PANEL_CLASS_NAME } from '../../../components/popupPanel/PopupPanel';
import { SheetId } from '../../structural/sheet/sheetTypes';

/**
 * WEB-13214 - Determine whether the action should still be triggered.
 *
 * If, for example, an ELEMENT_EDIT_START action is triggered, we want to ensure that it hasn't been
 * followed by an ELEMENTS_DESELECT_ALL action while the scroll animation is in progress.
 * This might happen when the user triple taps on the element to edit - the third tap might fall outside
 * the element, and land on the canvas, which will deselect the element.
 * Previously, this would result in the element being deselected, but the edit action still being triggered,
 * resulting in a deselected element in the edit mode.
 */
const shouldTriggerAction = (action: UnknownAction, initialState: any, currentState: any): boolean => {
    if (action.type !== ELEMENT_EDIT_START) return true;

    // WEB-13214 - If the selected elements have changed since we started the scroll on edit and when
    //  we're going to trigger the action, we don't want to trigger the edit start action.
    const initialSelectedIds = getSelectedElementIds(initialState);
    const currentSelectedIds = getSelectedElementIds(currentState);

    if (initialSelectedIds === currentSelectedIds) return true;

    // If the size increases from 0 to 1, it might be an element that didn't need to start as selected
    // prior to editing, such as a comment thread.
    if (length(initialSelectedIds) !== 0 || length(currentSelectedIds) !== 1) return false;

    const nowSelectedElementId = prop(0, currentSelectedIds);
    return nowSelectedElementId === action.id;
};

const shouldScrollOnEditStart = (state: any, action: UnknownAction) => {
    if (action.preventVirtualKeyboard) return false;

    if (action.editorKey === DOCUMENT_MODAL_TITLE_EDITOR_KEY) return false;

    // On tables on ipad, to prevent virtual keyboard from opening/closing too often as user edits between cells:
    // - ELEMENT_EDIT_START on TABLED_GRID_EDITOR_KEY is fired when user selects a table cell (virtual keyboard shows)
    // - ELEMENT_EDIT_START on TABLE_CELL_EDITOR_KEY is fired when user edits a table cell
    //
    // This way, going in between different cells will keep the virtual keyboard open.
    //
    // When the user first selects a table cell (when virtual keyboard shows), we want to scroll to the cell.
    // But afterwards, we only want to scroll when editing the cell.
    if (action.editorKey === TABLE_GRID_EDITOR_KEY) {
        return getCurrentlyEditingEditorKey(state) !== TABLE_CELL_EDITOR_KEY;
    }

    return true;
};

/**
 * Scroll to element when the user starts editing an element
 */
const getScrollConfigForElementEditStart = (state: any, action: UnknownAction): ScrollToElementConfig | undefined => {
    const elementId = action.id as string;
    const editorKey = action.editorKey as string;

    if (!shouldScrollOnEditStart(state, action)) return;

    const domElement = getContentDomElement(elementId, editorKey);
    const scrollableParent = getParentScrollableSection(domElement);
    if (!domElement || !scrollableParent) return;

    const isInPopupPanel = scrollableParent.closest(`.${POPUP_PANEL_CLASS_NAME}`);
    if (isInPopupPanel) return;

    const scrollTargetRect = domElement.getBoundingClientRect();

    const editorFocusRect = action.editorFocusRect as Rect | undefined;

    const toolbarHeight = selectIsMobileAppMode(state) ? MOBILE_TOOLBAR_HEIGHT[ToolbarLevel.PRIMARY] : 0;
    const bottomObstructionHeight = virtualKeyboardHeightSingleton.getMaxHeight() + toolbarHeight;

    return {
        scrollableParent,

        scrollTargetRect,
        editorFocusRect,

        bottomObstructionHeight,
    };
};

/**
 * When the user updates the active snap point of a sheet:
 * - Apply scroll excess padding to the bottom of the scrollable parent to allow the bottom portion of the
 *   scrollable parent to be accessible
 * - If the sheet config specifies to scroll to the selected element, scroll to it
 */
const getScrollConfigForSheetUpdateActiveSnapPoint = (
    state: any,
    action: UnknownAction,
): ScrollToElementConfig | undefined => {
    if (action.type === SheetActionTypes.SHEET_UPDATE_ACTIVE_SNAP_POINT) {
        const { sheetId, instanceId, activeSnapPoint } = action as SheetUpdateActiveSnapPointAction;

        const sheetState = getSheetState(sheetId, instanceId)(state);
        if (!sheetState || sheetState.activeSnapPoint !== 0) return;

        const bottomObstructionHeight = getSnapPointSheetHeight(activeSnapPoint);

        // If the sheet config specifies to scrollToElementOnOpen, we want to scroll to the selected element
        if (sheetState.config?.scrollToElementOnOpen) {
            const selectedElementId = action.instanceId || prop(0, getSelectedElementIds(state));

            const domElement = getContentDomElement(selectedElementId);
            const scrollableParent = getParentScrollableSection(domElement);
            if (!domElement || !scrollableParent) return;

            const scrollTargetRect = domElement.getBoundingClientRect();

            return {
                scrollableParent,
                scrollTargetRect,
                bottomObstructionHeight,
            };
        }

        // Otherwise, add scroll excess padding to the bottom of the scrollable parent to allow users
        // to scroll to the bottom of the parent when the sheet is open
        const scrollableParent = document.getElementById(CANVAS_VIEWPORT_ID);
        if (!scrollableParent) return;

        return {
            scrollableParent,
            bottomObstructionHeight,
        };
    }
};

const getScrollConfigForAction = (state: any, action: UnknownAction): ScrollToElementConfig | undefined => {
    switch (action.type) {
        case ELEMENT_EDIT_START:
            return getScrollConfigForElementEditStart(state, action);
        case SheetActionTypes.SHEET_UPDATE_ACTIVE_SNAP_POINT:
            return getScrollConfigForSheetUpdateActiveSnapPoint(state, action);
    }
};

const getScrollCleanupConfigForAction = (state: any, action: UnknownAction): ScrollCleanupConfig | undefined => {
    if (action.type === SheetActionTypes.SHEET_CLOSE) {
        return { sheetId: action.sheetId as SheetId };
    }

    let elementId;

    if (action.type === ELEMENT_EDIT_COMPLETE) {
        const editorKey = getCurrentlyEditingEditorKey(state);
        if (editorKey === DOCUMENT_MODAL_EDITOR_KEY)
            return { scrollableParent: document.getElementById('document-modal-content-scrollable-container') };

        elementId = getCurrentlyEditingId(state);
    }

    if (action.type === ELEMENTS_DESELECTED) {
        elementId = get(action.ids, 0);
    }

    if (action.type === ELEMENTS_DESELECT_ALL) {
        elementId = get(getSelectedElementIds(state), 0);
    }

    return elementId && { scrollableParent: getParentScrollableSectionForElement(elementId) };
};

/**
 * This middleware is responsible for:
 * - scrolling to the edited element on an edit start or element-specific sheet opening
 * - applying scroll excess padding to the bottom of the scrollable element to allow the bottom portion of the
 *   scrollable element to be accessible when the keyboard/sheet is shown, and resets it when the user finishes editing
 *
 * This needs to be in the middleware because we want to scroll to element before a caret is applied to the element,
 * which on Safari mobile web, will cause the whole webpage (not just canvas) to scroll to the caret position,
 * and cause the page to be misaligned.
 */
export default (store: ReduxStore) => (next: Function) => async (action: UnknownAction) => {
    if (action.remote) return next(action);

    const state = store.getState();

    // Get scroll config applied for certain actions, such as when user edits an element or keyboard/sheet shows up
    const scrollConfig = getScrollConfigForAction(state, action);

    // If bottomObstructionHeight defined, apply scroll excess padding to the bottom of the scrollable element
    // to allow the bottom portion of the scrollable element to be accessible
    if (scrollConfig?.bottomObstructionHeight) {
        const sheetId = action.sheetId as SheetId | undefined;
        await scrollExcessHandler.apply(scrollConfig, sheetId);
    }

    // If scrollTargetRect is defined, animate scroll to the element
    if (scrollConfig?.scrollTargetRect) {
        const { scrollableParent, scrollTargetRect } = scrollConfig;

        const getTargetScrollToElement = selectIsMobileAppMode(state)
            ? getTargetScrollToAlignElementToCenter
            : getTargetScrollToAlignElementToEdge;

        const targetScroll = getTargetScrollToElement({ ...scrollConfig, scrollTargetRect });

        await animateScrollPromise({
            scrollableParent,
            fromScrollLeft: scrollableParent.scrollLeft,
            fromScrollTop: scrollableParent.scrollTop,
            toScrollLeft: targetScroll.scrollLeft,
            toScrollTop: targetScroll.scrollTop,
            interpolationFactor: 0.2,
        });
    }

    const scrollCleanupConfig = getScrollCleanupConfigForAction(state, action);
    if (scrollCleanupConfig) {
        scrollExcessHandler.cleanup(scrollCleanupConfig);
    }

    // If something has changed from the start of this processing to now, we might no longer
    //  want to trigger the action.
    if (!shouldTriggerAction(action, state, store.getState())) return;

    next(action);
};
